import React from "react";

import { AgentPeek } from "@/components/brand";
import type { IntakeControlsLayout } from "../types";

type Props = {
  onClick: () => void;
  isDarkBg: boolean;
  /** Draws attention once, when Casey has something waiting to be reviewed. */
  badgeCount?: number;
  label: string;
  /** What the intake's own controls occupy, so the launcher can sit beside them. */
  intakeControls: IntakeControlsLayout;
};

/**
 * How far in from the right edge the launcher sits, so the intake's own controls
 * keep the corner to themselves.
 *
 * Those controls sit 24px in from the edge and each segment of their pill is
 * 44px, plus 8px between the two clusters so they read as neighbours rather than
 * one control. The step arrows are hidden below `lg` on the client's view, which
 * is why the wide offset is a breakpoint variant rather than its own value.
 */
const RIGHT_OFFSET: Record<IntakeControlsLayout, string> = {
  hidden: "right-6",
  "settings-only": "right-19",
  "with-step-arrows": "right-19 lg:right-41"
};

/**
 * The one always-visible piece of Casey.
 *
 * It sits on the same line as the intake's controls and immediately to their
 * left, built to their measurements -- same height, radius, and shadow -- so the
 * corner reads as one row of controls rather than a button that happens to float
 * nearby. Sizing it to match is what lets it sit there without crowding the
 * chevrons a client uses to move through the form. Casey stands on the pill's
 * bottom edge and pops up out of it when the launcher appears.
 */
export function CaseyLauncher({
  onClick,
  isDarkBg,
  badgeCount,
  label,
  intakeControls
}: Props) {
  const surface = isDarkBg
    ? "bg-white text-[#0f172a]"
    : "bg-[#0f172a] text-white";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`group fixed bottom-8 z-1400 flex h-11 items-center gap-2 rounded-lg pl-3.5 pr-4 shadow-[0_12px_28px_rgba(32,28,209,0.22)] transition-transform duration-150 ease-out hover:-translate-y-0.5 active:translate-y-0 ${RIGHT_OFFSET[intakeControls]} ${surface}`}
    >
      <span className="relative shrink-0 self-end">
        <span className="agent-peek-stage block [--peek-pop-delay:140ms]">
          <AgentPeek className="h-8" />
        </span>
        {badgeCount ? (
          <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#ef4444] px-1 text-xs font-bold leading-none text-white">
            {badgeCount}
          </span>
        ) : null}
      </span>
      <span className="whitespace-nowrap text-md font-semibold leading-none">
        {label}
      </span>
    </button>
  );
}
